import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Gauge, Ticket, Users, UserCheck, PlusCircle, LogOut } from 'lucide-react';
import useAdminUser from '../../hooks/useAdminUser';
import { ADMIN_NAVBAR_STRINGS, COMMON } from '../../constants/appConstants';
import { ADMIN_ROUTES } from '../../constants/routeConstants';
import styles from './AdminNavbar.module.scss';

const AdminNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { adminUser, isAdmin, isStaff, logout } = useAdminUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mgmtOpen, setMgmtOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const mgmtRef = useRef(null);
  const userRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate(ADMIN_ROUTES.LOGIN);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (mgmtRef.current && !mgmtRef.current.contains(e.target)) setMgmtOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <nav className={styles.adminNavbar}>
      <div className={styles.navContainer}>
        {/* Brand */}
        <Link to={ADMIN_ROUTES.DASHBOARD} className={styles.navBrand}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className={styles.brandIcon}>
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
          </svg>
          <div>
            <span className={styles.brandName}>{COMMON.BRAND_NAME}</span>
            <span className={styles.brandRole}>
              {isAdmin ? ADMIN_NAVBAR_STRINGS.ROLES.ADMIN : isStaff ? ADMIN_NAVBAR_STRINGS.ROLES.STAFF : ADMIN_NAVBAR_STRINGS.ROLES.DEFAULT}
            </span>
          </div>
        </Link>

        {/* Hamburger */}
        <button className={styles.menuToggle} onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle navigation">
          <span /><span /><span />
        </button>

        {/* Nav Content */}
        <div className={`${styles.navContent} ${menuOpen ? styles.open : ''}`}>
          <div className={styles.navLinks}>
            <Link to={ADMIN_ROUTES.DASHBOARD} className={`${styles.navLink} ${isActive(ADMIN_ROUTES.DASHBOARD) ? styles.active : ''}`}>
              <Gauge size={16} /> {ADMIN_NAVBAR_STRINGS.NAV.DASHBOARD}
            </Link>

            <Link to={ADMIN_ROUTES.TICKETS} className={`${styles.navLink} ${isActive(ADMIN_ROUTES.TICKETS) ? styles.active : ''}`}>
              <Ticket size={16} /> {ADMIN_NAVBAR_STRINGS.NAV.TICKETS}
            </Link>

            {isAdmin && (
              <div className={styles.dropdown} ref={mgmtRef}>
                <button className={`${styles.navLink}`} onClick={() => setMgmtOpen(!mgmtOpen)}>
                  <Users size={16} /> {ADMIN_NAVBAR_STRINGS.NAV.MANAGEMENT} ▾
                </button>
                {mgmtOpen && (
                  <div className={styles.dropdownMenu}>
                    <Link to={ADMIN_ROUTES.USERS} className={styles.dropdownItem} onClick={() => setMgmtOpen(false)}>
                      <Users size={14} /> {ADMIN_NAVBAR_STRINGS.NAV.USERS}
                    </Link>
                    <Link to={ADMIN_ROUTES.EMPLOYEES} className={styles.dropdownItem} onClick={() => setMgmtOpen(false)}>
                      <UserCheck size={14} /> {ADMIN_NAVBAR_STRINGS.NAV.EMPLOYEES}
                    </Link>
                  </div>
                )}
              </div>
            )}

            {isStaff && (
              <Link to={ADMIN_ROUTES.ASSIGN} className={`${styles.navLink} ${isActive(ADMIN_ROUTES.ASSIGN) ? styles.active : ''}`}>
                <PlusCircle size={16} /> {ADMIN_NAVBAR_STRINGS.NAV.ASSIGN_TICKETS}
              </Link>
            )}
          </div>

          <div className={styles.navRight}>
            {/* User Dropdown */}
            <div className={styles.dropdown} ref={userRef}>
              <button className={styles.userBtn} onClick={() => setUserOpen(!userOpen)}>
                <div className={styles.userAvatar}>
                  {adminUser?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className={`${styles.userInfo} ${styles.desktopOnly}`}>
                  <div className={styles.userName}>{adminUser?.name}</div>
                  <div className={styles.userRole}>
                    {isAdmin ? ADMIN_NAVBAR_STRINGS.ROLES.ADMIN_LABEL : isStaff ? ADMIN_NAVBAR_STRINGS.ROLES.STAFF_LABEL : ADMIN_NAVBAR_STRINGS.ROLES.USER_LABEL}
                  </div>
                </div>
              </button>
              {userOpen && (
                <div className={styles.dropdownMenu}>
                  <div className={styles.dropdownHeader}>
                    <div className={styles.headerName}>{adminUser?.name}</div>
                    <div className={styles.headerEmail}>{adminUser?.email}</div>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <Link to={ADMIN_ROUTES.DASHBOARD} className={styles.dropdownItem} onClick={() => setUserOpen(false)}>
                    <Gauge size={14} /> {ADMIN_NAVBAR_STRINGS.NAV.DASHBOARD}
                  </Link>
                  <Link to={ADMIN_ROUTES.TICKETS} className={styles.dropdownItem} onClick={() => setUserOpen(false)}>
                    <Ticket size={14} /> {ADMIN_NAVBAR_STRINGS.NAV.MY_TICKETS}
                  </Link>
                  <div className={styles.dropdownDivider} />
                  <button className={`${styles.dropdownItem} ${styles.danger}`} onClick={handleLogout}>
                    <LogOut size={14} /> {ADMIN_NAVBAR_STRINGS.NAV.LOGOUT}
                  </button>
                </div>
              )}
            </div>

            {isStaff && (
              <button className={`btn btn-primary btn-sm ${styles.desktopOnly}`} onClick={() => navigate(ADMIN_ROUTES.TICKETS)}>
                <Ticket size={14} /> {ADMIN_NAVBAR_STRINGS.NAV.VIEW_ALL_TICKETS}
              </button>
            )}

            <div className={styles.mobileLogout}>
              <button onClick={handleLogout}>
                <LogOut size={14} /> {ADMIN_NAVBAR_STRINGS.NAV.LOGOUT}
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AdminNavbar;